import React, { useState, useEffect } from "react";
import { FaEye, FaEdit, FaTrash, FaSearch, FaPlus, FaTimes } from "react-icons/fa";
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import useAxios from "../../api/useAxios";
import Swal from 'sweetalert2';

const SubCategories = () => {
  const axiosInstance = useAxios();
  const [subCategories, setSubCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editSubCategoryId, setEditSubCategoryId] = useState(null);
  const [newSubCategory, setNewSubCategory] = useState({
    name: "",
    description: "",
    categoryId: "",
    active: true,
  });
  const [viewSubCategory, setViewSubCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [currentSubCategoryDetails, setCurrentSubCategoryDetails] = useState(null);

  // Fetch All SubCategories
  const fetchSubCategories = async () => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.get("/subcategories");
      setSubCategories(res.data.data);
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Sub-categories loaded successfully',
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: 'top-right'
      });
    } catch (err) {
      console.error("Error fetching sub-categories:", err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load sub-categories',
        timer: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch All Categories for dropdown
  const fetchCategories = async () => {
    try {
      const res = await axiosInstance.get("/categories");
      setCategories(res.data.data);
    } catch (err) {
      console.error("Error fetching categories:", err);
      Swal.fire({
        icon: 'warning',
        title: 'Warning',
        text: 'Failed to load categories. Some features may be limited.',
        timer: 3000,
      });
    }
  };

  // Fetch SubCategory by ID
  const fetchSubCategoryById = async (id) => {
    try {
      const res = await axiosInstance.get(`/subcategories/${id}`);
      return res.data.data;
      
    } catch (err) {
      console.error("Error fetching sub-category by ID:", err);
      throw err;
    }
  };

  useEffect(() => {
    fetchSubCategories();
    fetchCategories();
  }, []);

  // Sorting function
  const sortedSubCategories = [...subCategories].sort((a, b) => {
    if (sortConfig.key === 'categoryName') {
      const categoryA = a.categoryName || '';
      const categoryB = b.categoryName || '';
      if (categoryA < categoryB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (categoryA > categoryB) return sortConfig.direction === 'asc' ? 1 : -1;
    }
    
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Filter & Pagination
  const filteredSubCategories = sortedSubCategories.filter((subCategory) => {
    // Text search filter
    const searchMatch = 
      subCategory.name.toLowerCase().includes(search.toLowerCase()) ||
      (subCategory.description && subCategory.description.toLowerCase().includes(search.toLowerCase())) ||
      (subCategory.categoryName && subCategory.categoryName.toLowerCase().includes(search.toLowerCase()));
    
    // Status filter
    const statusMatch = statusFilter === "all" || 
      (statusFilter === "active" && subCategory.active) || 
      (statusFilter === "inactive" && !subCategory.active);
    
    // Category filter
    const categoryMatch = categoryFilter === "all" || 
      (subCategory.categoryName && subCategory.categoryName.toLowerCase().includes(categoryFilter.toLowerCase()));
    
    return searchMatch && statusMatch && categoryMatch;
  });

  const totalEntries = filteredSubCategories.length;
  const totalPages = Math.ceil(totalEntries / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalEntries);
  const currentSubCategories = filteredSubCategories.slice(startIndex, endIndex);

  // Handle Sort
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Handle Create SubCategory
  const handleCreateSubCategory = async (subCategoryData) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post("/subcategories", subCategoryData);
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Sub-category created successfully',
        timer: 3000,
        showConfirmButton: false,
      });
      return response.data.data;
    } catch (err) {
      console.error("Error creating sub-category:", err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || 'Failed to create sub-category',
        timer: 3000,
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Update SubCategory
  const handleUpdateSubCategory = async (id, subCategoryData) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.put(`/subcategories/${id}`, subCategoryData);
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Sub-category updated successfully',
        timer: 3000,
        showConfirmButton: false,
      });
      return response.data.data;
    } catch (err) {
      console.error("Error updating sub-category:", err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || 'Failed to update sub-category',
        timer: 3000,
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Save (Create/Update)
  const handleSaveSubCategory = async () => {
    if (!newSubCategory.name.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Sub-category name is required',
        timer: 3000,
      });
      return;
    }

    if (!newSubCategory.categoryId) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please select a parent category',
        timer: 3000,
      });
      return;
    }

    const subCategoryData = {
      name: newSubCategory.name.trim(),
      description: newSubCategory.description?.trim() || "",
      active: newSubCategory.active,
      categoryId: parseInt(newSubCategory.categoryId)
    };

    try {
      if (editSubCategoryId) {
        await handleUpdateSubCategory(editSubCategoryId, subCategoryData);
      } else {
        await handleCreateSubCategory(subCategoryData);
      }
      
      setShowModal(false);
      setEditSubCategoryId(null);
      setNewSubCategory({ 
        name: "", 
        description: "", 
        categoryId: "", 
        active: true 
      });
      fetchSubCategories();
    } catch (err) {
      // Error already handled in individual functions
    }
  };

  // Handle Delete SubCategory
  const handleDeleteSubCategory = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      customClass: {
        confirmButton: 'swal2-confirm',
        cancelButton: 'swal2-cancel'
      }
    });

    if (result.isConfirmed) {
      setIsLoading(true);
      try {
        await axiosInstance.delete(`/subcategories/${id}`);
        
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Sub-category has been deleted.',
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: 'top-right'
        });
        
        fetchSubCategories();
      } catch (err) {
        console.error("Error deleting sub-category:", err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.response?.data?.message || 'Failed to delete sub-category',
          timer: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Toggle Status (using PUT update since no dedicated status endpoint)
  const handleToggleStatus = async (subCategory) => {
    const newStatus = !subCategory.active;
    
    const result = await Swal.fire({
      title: 'Change Status',
      text: `Are you sure you want to ${newStatus ? 'activate' : 'deactivate'} "${subCategory.name}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#6c757d',
      confirmButtonText: `Yes, ${newStatus ? 'activate' : 'deactivate'}`,
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        // Find the parent category
        const parentCategory = categories.find(c => c.name === subCategory.categoryName);
        
        if (!parentCategory) {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Parent category not found',
            timer: 3000,
          });
          return;
        }

        const subCategoryData = {
          name: subCategory.name,
          description: subCategory.description || "",
          active: newStatus,
          categoryId: parentCategory.id
        };

        await handleUpdateSubCategory(subCategory.id, subCategoryData);
        
        // Update local state immediately for better UX
        setSubCategories(prevSubCategories => 
          prevSubCategories.map(sc => 
            sc.id === subCategory.id 
              ? { ...sc, active: newStatus } 
              : sc
          )
        );
      } catch (err) {
        // Error already handled in handleUpdateSubCategory
      }
    }
  };

  // Open view modal and fetch fresh data
  const openViewModal = async (subCategory) => {
    try {
      setIsLoading(true);
      const freshData = await fetchSubCategoryById(subCategory.id);
      setViewSubCategory(freshData);
      setShowViewModal(true);
    } catch (err) {
      // Use cached data if fetch fails
      setViewSubCategory(subCategory);
      setShowViewModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Open edit modal with sub-category data
  const openEditModal = async (subCategory) => {
    try {
      setIsLoading(true);
      const freshData = await fetchSubCategoryById(subCategory.id);
      
      // Find the category ID from categories list based on categoryName
      const category = categories.find(c => c.name === freshData.categoryName);
      
      setNewSubCategory({
        name: freshData.name,
        description: freshData.description || "",
        categoryId: category ? category.id.toString() : "",
        active: freshData.active,
      });
      setEditSubCategoryId(freshData.id);
      setCurrentSubCategoryDetails(freshData);
      setShowModal(true);
    } catch (err) {
      // Use cached data if fetch fails
      const category = categories.find(c => c.name === subCategory.categoryName);
      
      setNewSubCategory({
        name: subCategory.name,
        description: subCategory.description || "",
        categoryId: category ? category.id.toString() : "",
        active: subCategory.active,
      });
      setEditSubCategoryId(subCategory.id);
      setCurrentSubCategoryDetails(subCategory);
      setShowModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset modal
  const resetModal = () => {
    setNewSubCategory({ 
      name: "", 
      description: "", 
      categoryId: "", 
      active: true 
    });
    setEditSubCategoryId(null);
    setCurrentSubCategoryDetails(null);
  };

  // Get unique categories from sub-categories for filter
  const getUniqueCategories = () => {
    const categoryNames = subCategories
      .map(sc => sc.categoryName)
      .filter((name, index, arr) => name && arr.indexOf(name) === index);
    
    return categoryNames.sort();
  };

  // Format date if exists
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return "N/A";
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  // Get active categories only
  const getActiveCategories = () => {
    return categories.filter(c => c.active);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      {/* HEADER */}
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Sub-Category Management</h1>
        <p className="text-gray-600">Manage your sub-category details and parent category associations</p>
      </div>

      {/* CONTROLS CARD */}
      <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 mb-6 border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex flex-col md:flex-row gap-4 flex-1 w-full">
            {/* SEARCH */}
            <div className="relative flex-1 max-w-md">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search sub-categories by name, description, or parent category..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-12 pr-4 py-2.5 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <FaTimes />
                </button>
              )}
            </div>

            {/* FILTERS ROW */}
            <div className="flex flex-wrap gap-3">
              {/* CATEGORY FILTER */}
              <div className="relative min-w-[150px]">
                <select
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="appearance-none px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white pr-10 w-full"
                >
                  <option value="all">All Parent Categories</option>
                  {getUniqueCategories().map((categoryName) => (
                    <option key={categoryName} value={categoryName}>
                      {categoryName}
                    </option>
                  ))}
                </select>
                <IoMdArrowDropdown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
              </div>

              {/* STATUS FILTER */}
              <div className="relative min-w-[150px]">
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="appearance-none px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white pr-10 w-full"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active Only</option>
                  <option value="inactive">Inactive Only</option>
                </select>
                <IoMdArrowDropdown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
              </div>

              {/* ROWS PER PAGE */}
              <div className="flex items-center gap-3 min-w-[150px]">
                <div className="relative w-full">
                  <select
                    value={rowsPerPage}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      if (value > 0) {
                        setRowsPerPage(value);
                        setCurrentPage(1);
                      }
                    }}
                    className="appearance-none px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white pr-10 w-full"
                  >
                    {[5, 10, 20, 50].map((num) => (
                      <option key={num} value={num}>
                        {num} rows
                      </option>
                    ))}
                  </select>
                  <IoMdArrowDropdown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>

          {/* ADD BUTTON */}
          <button
            onClick={() => {
              resetModal();
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-5 py-2.5 md:py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg whitespace-nowrap w-full md:w-auto justify-center"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <>
                <FaPlus />
                <span>Add New Sub-Category</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* STATUS SUMMARY */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-blue-500">
          <div className="text-sm text-gray-500">Total Sub-Categories</div>
          <div className="text-2xl font-bold text-gray-800">{subCategories.length}</div>
          <div className="text-xs text-gray-400 mt-1">All sub-categories</div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-green-500">
          <div className="text-sm text-gray-500">Active Sub-Categories</div>
          <div className="text-2xl font-bold text-gray-800">
            {subCategories.filter(sc => sc.active).length}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {subCategories.length > 0 ? `${Math.round((subCategories.filter(sc => sc.active).length / subCategories.length) * 100)}% of total` : '0%'}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-red-500">
          <div className="text-sm text-gray-500">Inactive Sub-Categories</div>
          <div className="text-2xl font-bold text-gray-800">
            {subCategories.filter(sc => !sc.active).length}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {subCategories.length > 0 ? `${Math.round((subCategories.filter(sc => !sc.active).length / subCategories.length) * 100)}% of total` : '0%'}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-purple-500">
          <div className="text-sm text-gray-500">Parent Categories</div>
          <div className="text-2xl font-bold text-gray-800">
            {getUniqueCategories().length}
          </div>
          <div className="text-xs text-gray-400 mt-1">Across all sub-categories</div>
        </div>
      </div>

      {/* MAIN TABLE CARD */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
        {/* TABLE HEADER */}
        <div className="px-4 md:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Sub-Category Details</h2>
              <div className="text-sm text-gray-600 mt-1">
                {statusFilter === "all" ? "Showing all sub-categories" : 
                 statusFilter === "active" ? "Showing active sub-categories only" : 
                 "Showing inactive sub-categories only"}
                {categoryFilter !== "all" && ` under ${categoryFilter}`}
              </div>
            </div>
            <div className="text-sm text-gray-500">
              {totalEntries > 0 && `Total: ${totalEntries} sub-categories`}
            </div>
          </div>
        </div>

        {/* TABLE CONTAINER */}
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="py-20 flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">Loading sub-categories...</p>
            </div>
          ) : (
            <>
              <table className="w-full min-w-max">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 md:px-6 py-4 text-left font-semibold text-gray-700">#</th>
                    <th 
                      className="px-4 md:px-6 py-4 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-2">
                        Sub-Category Name
                        {sortConfig.key === 'name' && (
                          sortConfig.direction === 'asc' ? <IoMdArrowDropup /> : <IoMdArrowDropdown />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-4 md:px-6 py-4 text-left font-semibold text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleSort('categoryName')}
                    >
                      <div className="flex items-center gap-2">
                        Parent Category
                        {sortConfig.key === 'categoryName' && (
                          sortConfig.direction === 'asc' ? <IoMdArrowDropup /> : <IoMdArrowDropdown />
                        )}
                      </div>
                    </th>
                    <th className="px-4 md:px-6 py-4 text-left font-semibold text-gray-700">Description</th>
                    <th className="px-4 md:px-6 py-4 text-left font-semibold text-gray-700">Status</th>
                    <th className="px-4 md:px-6 py-4 text-center font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentSubCategories.map((subCategory, index) => (
                    <tr 
                      key={subCategory.id} 
                      className={`border-b border-gray-100 hover:bg-blue-50 transition-colors group ${
                        !subCategory.active ? "bg-red-50 hover:bg-red-100" : ""
                      }`}
                    >
                      <td className="px-4 md:px-6 py-4">
                        <div className="text-gray-600 font-medium">{startIndex + index + 1}</div>
                      </td>
                      <td className="px-4 md:px-6 py-4">
                        <div>
                          <div className="font-semibold text-gray-900 group-hover:text-blue-700">{subCategory.name}</div>
                          <div className="text-xs text-gray-500">ID: {subCategory.id}</div>
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-4">
                        <div className="font-medium text-gray-700">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                            {subCategory.categoryName || "No Parent Category"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-4">
                        <div className="max-w-xs">
                          <p className="text-gray-700 line-clamp-2">
                            {subCategory.description || (
                              <span className="text-gray-400 italic">No description</span>
                            )}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-4">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleToggleStatus(subCategory)}
                            disabled={isLoading}
                            className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                              subCategory.active 
                                ? "bg-green-500 hover:bg-green-600 focus:ring-green-500" 
                                : "bg-gray-300 hover:bg-gray-400 focus:ring-gray-400"
                            } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span
                              className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                                subCategory.active ? "translate-x-6" : "translate-x-1"
                              }`}
                            />
                          </button>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            subCategory.active 
                              ? "bg-green-100 text-green-800" 
                              : "bg-red-100 text-red-800"
                          }`}>
                            {subCategory.active ? "Active" : "Inactive"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-4">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => openViewModal(subCategory)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors hover:scale-110"
                            title="View Details"
                            disabled={isLoading}
                          >
                            <FaEye size={18} />
                          </button>
                          <button
                            onClick={() => openEditModal(subCategory)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors hover:scale-110"
                            title="Edit Sub-Category"
                            disabled={isLoading}
                          >
                            <FaEdit size={18} />
                          </button>
                          <button
                            onClick={() => handleDeleteSubCategory(subCategory.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors hover:scale-110"
                            title="Delete Sub-Category"
                            disabled={isLoading}
                          >
                            <FaTrash size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* EMPTY STATE */}
              {!isLoading && currentSubCategories.length === 0 && (
                <div className="py-16 text-center">
                  <div className="text-gray-400 mb-4">
                    <FaSearch size={48} className="mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-600 mb-2">
                    No sub-categories found
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto">
                    {search || statusFilter !== "all" || categoryFilter !== "all"
                      ? "Try adjusting your search or filter terms" 
                      : "Add your first sub-category to get started"}
                  </p>
                  {!search && statusFilter === "all" && categoryFilter === "all" && (
                    <button
                      onClick={() => {
                        resetModal();
                        setShowModal(true);
                      }}
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Create Your First Sub-Category
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* PAGINATION */}
        {totalEntries > 0 && totalPages > 1 && (
          <div className="px-4 md:px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-600">
                Showing <span className="font-semibold">{startIndex + 1}</span> to{" "}
                <span className="font-semibold">{endIndex}</span> of{" "}
                <span className="font-semibold">{totalEntries}</span> sub-categories
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-lg border transition-colors flex items-center gap-2 ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Previous
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={i}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-1 min-w-[40px] rounded transition-colors ${
                          currentPage === pageNum
                            ? "bg-blue-600 text-white shadow-sm"
                            : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 rounded-lg border transition-colors flex items-center gap-2 ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400"
                  }`}
                >
                  Next
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ADD/EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md animate-scale-in">
            {/* MODAL HEADER */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    {editSubCategoryId ? "Edit Sub-Category" : "Add New Sub-Category"}
                  </h3>
                  {editSubCategoryId && currentSubCategoryDetails && (
                    <p className="text-sm text-gray-600 mt-1">
                      ID: {currentSubCategoryDetails.id}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetModal();
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  disabled={isLoading}
                >
                  <FaTimes size={20} />
                </button>
              </div>
            </div>

            {/* MODAL BODY */}
            <div className="p-6 space-y-4">
              {/* SUB-CATEGORY NAME */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sub-Category Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter sub-category name"
                  value={newSubCategory.name}
                  onChange={(e) => setNewSubCategory({ ...newSubCategory, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  disabled={isLoading}
                />
              </div>

              {/* PARENT CATEGORY SELECTION */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Parent Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={newSubCategory.categoryId}
                  onChange={(e) => setNewSubCategory({ ...newSubCategory, categoryId: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  disabled={isLoading || categories.length === 0}
                >
                  <option value="">Select a parent category</option>
                  {getActiveCategories().map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name} {category.brandName ? `(${category.brandName})` : ''}
                    </option>
                  ))}
                </select>
                {categories.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">No categories available. Please add a category first.</p>
                )}
                {getActiveCategories().length === 0 && categories.length > 0 && (
                  <p className="text-xs text-yellow-500 mt-1">No active categories available. All parent categories are inactive.</p>
                )}
              </div>

              {/* DESCRIPTION */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-gray-400 text-xs">(Optional)</span>
                </label>
                <textarea
                  placeholder="Enter sub-category description"
                  value={newSubCategory.description}
                  onChange={(e) => setNewSubCategory({ ...newSubCategory, description: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-none"
                  disabled={isLoading}
                />
              </div>

              {/* STATUS */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={newSubCategory.active}
                  onChange={(e) =>
                    setNewSubCategory({ ...newSubCategory, active: e.target.value === "true" })
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  disabled={isLoading}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>

            {/* MODAL FOOTER */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetModal();
                  }}
                  className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSubCategory}
                  disabled={isLoading || !newSubCategory.name.trim() || !newSubCategory.categoryId}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 min-w-[120px] justify-center"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Processing...
                    </>
                  ) : editSubCategoryId ? (
                    "Update Sub-Category"
                  ) : (
                    "Create Sub-Category"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VIEW MODAL */}
      {showViewModal && viewSubCategory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md animate-scale-in">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">Sub-Category Details</h3>
                  <p className="text-sm text-gray-600 mt-1">ID: {viewSubCategory.id}</p>
                </div>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FaTimes size={20} />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">Name</label>
                    <p className="text-gray-900 font-medium text-lg">{viewSubCategory.name}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">Status</label>
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold ${
                      viewSubCategory.active 
                        ? "bg-green-100 text-green-800" 
                        : "bg-red-100 text-red-800"
                    }`}>
                      {viewSubCategory.active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
                
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">Parent Category</label>
                  <div className="mt-1">
                    <span className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                      {viewSubCategory.categoryName || "No Parent Category Assigned"}
                    </span>
                  </div>
                </div>
                
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">Description</label>
                  <div className="mt-1 p-4 bg-gray-50 rounded-lg">
                    <p className="text-gray-700">
                      {viewSubCategory.description || (
                        <span className="text-gray-400 italic">No description provided</span>
                      )}
                    </p>
                  </div>
                </div>
                
                {viewSubCategory.createAt && (
                  <div className="pt-4 border-t border-gray-200">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">Created Date</label>
                    <p className="text-gray-700">
                      {formatDate(viewSubCategory.createAt)}
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    openEditModal(viewSubCategory);
                  }}
                  className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <FaEdit size={16} />
                  Edit Sub-Category
                </button>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add custom styles for SweetAlert */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scale-in {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
        
        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
        
        .swal2-confirm {
          background: linear-gradient(to right, #3b82f6, #1d4ed8) !important;
        }
        
        .swal2-cancel {
          background: #6b7280 !important;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default SubCategories;